import React, { useCallback, useMemo, useState } from 'react';
import ParsedItemStats, { ParsedItemStatsType } from './ParsedItemStats';
import { Column, Row, Title } from '@ydistri/ds';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../../../../../store';
import { toEnumValue } from '../../../../../../../../../lib/utils/utils';
import RedistributionImportParsedItemsTable from './RedistributionImportParsedItemsTable';
import { StatisticsItem } from '../../../../../../../../ProjectAdministration/common/administrationItemsTypes';

const RedistributionImportParsingResultOverview: React.FC = () => {
  const parsedItems = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.importItems,
  );

  const errorsCount = useMemo(
    () =>
      parsedItems.reduce((acc, item) => {
        return acc + (item.isError ? 1 : 0);
      }, 0),
    [parsedItems],
  );

  // we need to distinguish between unselected and null (cleared) filter,
  // so we use undefined for unselected and null for cleared
  // if filter is unselected, and there are errors, we will show only errors by default
  const [filteredItemType, setFilteredItemType] = useState<
    ParsedItemStatsType | undefined | null
  >();

  /**
   * Handles the selection of a statistics item and filters the table based on the selected item type.
   * If the item is null, it resets the filter.
   * @param item - The selected statistics item or null to reset the filter.
   */
  const statsItemSelectedHandler = useCallback((item: StatisticsItem | null) => {
    if (item !== null) {
      const value = toEnumValue(ParsedItemStatsType, item.label);
      if (value) {
        setFilteredItemType(value);
        return;
      }
    }
    setFilteredItemType(null);
  }, []);

  /**
   * Derives the active filter:
   * - `undefined` (unselected) → auto-mode: default to ERROR when errors exist, otherwise no filter
   * - `null` (explicitly cleared) → no filter
   * - a value → use it if it yields results; if the selection would be empty, fall back to auto-mode
   */
  // eslint-disable-next-line @ydistri/react/no-primitive-usememo -- expensive computation
  const effectiveFilterType = useMemo((): ParsedItemStatsType | null => {
    if (filteredItemType === null) return null;

    if (filteredItemType !== undefined) {
      let wouldHaveResults: boolean;
      if (filteredItemType === ParsedItemStatsType.VALID) {
        wouldHaveResults = parsedItems.some(item => !item.isError);
      } else if (filteredItemType === ParsedItemStatsType.ERROR) {
        wouldHaveResults = parsedItems.some(item => item.isError);
      } else {
        wouldHaveResults = parsedItems.length > 0;
      }

      if (wouldHaveResults) {
        return filteredItemType;
      }
      // selected filter has no results → fall through to auto-mode
    }

    // auto-mode: highlight errors by default
    return errorsCount > 0 ? ParsedItemStatsType.ERROR : null;
  }, [filteredItemType, parsedItems, errorsCount]);

  const itemsFiltered = useMemo(() => {
    switch (effectiveFilterType) {
      case ParsedItemStatsType.VALID:
        return parsedItems.filter(item => !item.isError);
      case ParsedItemStatsType.ERROR:
        return parsedItems.filter(item => item.isError);
      case ParsedItemStatsType.TOTAL:
      case null:
      default:
        return parsedItems;
    }
  }, [effectiveFilterType, parsedItems]);

  if (parsedItems.length > 0) {
    return (
      <Column $gap="10px" $flexGrow={1}>
        <Row $alignItems="center" $gap="4rem">
          <ParsedItemStats
            items={parsedItems}
            errorsCount={errorsCount}
            onItemSelected={statsItemSelectedHandler}
            selectedItemType={effectiveFilterType}
          />
          {errorsCount > 0 && (
            <Title $size="large" $type="danger">
              Only valid items will be processed
            </Title>
          )}
        </Row>
        <RedistributionImportParsedItemsTable items={itemsFiltered} />
      </Column>
    );
  }
};

export default RedistributionImportParsingResultOverview;
